import { AB_DISCOVERY_NAV } from "@/constants/ABMng";
import { selectGlobalBannerHeight } from "@/context/modules/app";
import useAppStore from "@/context/store";
import { getUserBoards } from "@/services/user";
import useABTest from "@/utils/hooks/useABtest";
import { openBoardModal } from "@/utils/open";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import HBIcon from "../HBIcon";
import styles from "./DiscoveryBoardGuide.module.less";

const GUIDE_KEY = "discovery-board-guide-shown";

export default function DiscoveryBoardGuide() {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [hasBoards, setHasBoards] = useState(false);
  const [loading, setLoading] = useState(true);
  const guideRef = useRef(null);
  const targetRef = useRef(null);
  const resizeObserverRef = useRef(null);
  const router = useRouter();

  const globalBannerHeight = useAppStore(selectGlobalBannerHeight);
  const { user } = useAppStore(({ user }) => ({ user }));

  // AB 实验检查
  const discoveryNavTest = useABTest(AB_DISCOVERY_NAV);
  const showGuide = discoveryNavTest.new;

  // 检查用户是否有画板
  useEffect(() => {
    const checkUserBoards = async () => {
      if (!showGuide || !user?.urlname) {
        setLoading(false);
        return;
      }

      try {
        const result = await getUserBoards({
          username: user.urlname,
          limit: 1, // 只需要检查是否有画板，所以限制为1
        });
        const hasBoardsData = result.boards && result.boards.length > 0;
        setHasBoards(hasBoardsData);
      } catch (error) {
        console.error("Failed to fetch user boards:", error);
        setHasBoards(false);
      } finally {
        setLoading(false);
      }
    };

    checkUserBoards();
  }, [showGuide, user?.urlname]);

  // 检查是否需要显示引导
  useEffect(() => {
    if (loading || !showGuide || !user?.urlname) {
      return;
    }

    const hasShown = localStorage.getItem(GUIDE_KEY);

    if (!hasShown) {
      setIsVisible(true);
    }
  }, [loading, showGuide, user?.urlname]);

  const close = useCallback(() => {
    setIsVisible(false);
    // 记录用户已看过引导
    localStorage.setItem(GUIDE_KEY, "true");
  }, []);

  const handleTargetClick = useCallback(() => {
    close();
  }, [close]);

  const updatePosition = useCallback(() => {
    const targetElement = document.querySelector("#sidenav-item-inspiration");
    if (!targetElement) return;

    // 移除之前目标元素的事件监听
    if (targetRef.current) {
      targetRef.current.removeEventListener("click", handleTargetClick);
    }

    // 添加新目标元素的事件监听
    targetElement.addEventListener("click", handleTargetClick);
    targetRef.current = targetElement;

    const targetRect = targetElement.getBoundingClientRect();

    // 计算引导框的位置，放在目标元素的右侧
    const top = targetRect.top + targetRect.height / 2;
    const left = targetRect.right + 8;

    setPosition({ top, left });
  }, [handleTargetClick]);

  // 元素大小变化和位置变化监听
  useEffect(() => {
    if (!isVisible) return;

    // 断开之前的观察
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    const targetElement = document.querySelector("#sidenav-item-inspiration");

    if (targetElement && window.ResizeObserver) {
      // 创建新的 ResizeObserver 实例
      resizeObserverRef.current = new ResizeObserver(() => {
        updatePosition();
      });

      // 开始观察目标元素
      resizeObserverRef.current.observe(targetElement);

      // 初始化位置
      updatePosition();
    }

    return () => {
      // 清理目标元素的事件监听
      if (targetRef.current) {
        targetRef.current.removeEventListener("click", handleTargetClick);
      }
      // 断开 ResizeObserver
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [isVisible, globalBannerHeight, updatePosition, handleTargetClick]);

  const handleCreateBoard = useCallback(async () => {
    openBoardModal({
      name: "boardModal",
      onOk: (board) => {
        // 创建成功后跳转到画板页面
        router.push(`/boards/${board.board_id}`);
        // 关闭引导
        close();
      },
    });
  }, [router, close]);

  if (!isVisible || loading || !showGuide) return null;

  const description = hasBoards
    ? "灵感页现已升级为基于画板进行探索"
    : "灵感页现已升级为基于画板进行探索, 去创建画板开始灵感探索之旅吧!";

  return (
    <div
      ref={guideRef}
      className={styles.guideCard}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div className={styles.header}>
        <div className={styles.description}>{description}</div>
        <button onClick={close} className={styles.closeButton} type="button">
          <HBIcon type="ic_close" />
        </button>
      </div>
      {!hasBoards && (
        <div className={styles.footer}>
          <button
            onClick={handleCreateBoard}
            className={classNames(styles.button, styles.buttonPrimary)}
            data-gd-click="button_click"
            data-button-name="创建画板"
          >
            创建画板
          </button>
        </div>
      )}

      <svg
        className={styles.arrow}
        width="6"
        height="20"
        viewBox="0 0 6 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.58012 11.7169L4.62445 15.3996C5.51358 16.4751 6 17.827 6 19.2224L6 0.388485C6 1.78397 5.51358 3.13581 4.62446 4.21137L1.58012 7.89403C0.663094 9.00334 0.663093 10.6076 1.58012 11.7169Z"
          fill="#4D7CFF"
        />
      </svg>
    </div>
  );
}
